'use strict';
/* global dataLayer */

import Cookies from 'js-cookie';

class Loop54Connector {

	/**
	 * Constructor
	 *
	 * @param {Object} [settings] - Settings object
	 */
	constructor(settings) {

		this.loadDefaults();
		this.settings = settings;
	}

	/**
	 * Load default settings.
	 */
	loadDefaults() {
		this.settings = {
			url: '',
			apiVersion: 'V3',
			userId: false,
			storageKey: 'searchStorage'
		};
		this.setEnvironment();
	}

	/**
	 * Set settings.
	 *
	 * @param {Object} settings - Settings object.
	 */
	set settings(settings) {
		if (typeof settings === 'object' && settings) {
			this._settings = { ...(this._settings || {}), ...settings };
		}
	}

	/**
	 * Get settings.
	 *
	 * @returns {Object} - Settings object.
	 */
	get settings() {
		return this._settings;
	}

	/**
	 * Sets Loop54 environment and returns the environment url.
	 *
	 * @param {boolean} [devEnv=false] If true uses development environment.
	 * @returns {string} Environment url.
	 */
	setEnvironment(devEnv = false) {

		// Select env address (prod is always forced on live site)
		const devEnvAddr = 'https://intersport-fi-dev.54proxy.com';
		const prodEnvAddr = 'https://intersport-fi-prod.54proxy.com';

		const isDevUrl = document.location.host.includes('intersport-admin') || document.location.host.includes('vcap.me');
		this.settings.url = devEnv || isDevUrl ? devEnvAddr : prodEnvAddr;

		return this.settings.url;
	}


	writeData(object) {

		let storageType = localStorage;
		if (!storageType) {
			storageType = sessionStorage;
		}

		if (storageType) {
			try {
				storageType.setItem(this.settings.storageKey, JSON.stringify(object));
			} catch (error) {
				return false;
			}
			return true;
		} else {
			return false;
		}
	}

	readData() {
		let storageType = localStorage;
		if (!storageType) {
			storageType = sessionStorage;
		}

		if (storageType && storageType.getItem(this.settings.storageKey)) {
			try {
				return JSON.parse(storageType.getItem(this.settings.storageKey));
			} catch (error) {
				return false;
			}
		} else {
			return false;
		}
	}

	removeData() {

		let storageType = localStorage;
		if (!storageType) {
			storageType = sessionStorage;
		}

		if (storageType) {
			try {
				return JSON.parse(storageType.removeItem(this.settings.storageKey));
			} catch (error) {
				return false;
			}
		} else {
			return false;
		}
	}

	makeId(ownId) {
		let size = 12;
		let id = '';

		if (ownId) {
			id = ownId;
		} else {
			const possibleIdCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890';

			for (let i = 0; i < size; i++) {
				id += possibleIdCharacters.charAt(Math.floor(Math.random() * possibleIdCharacters.length));
			}
		}

		this.writeData({ id: id });

		return id;
	}

	getId() {

		if (this.settings.userId) {
			return this.settings.userId;
		}

		const userIdStage = Cookies.get('loggedIn')
		if (userIdStage) {
			return userIdStage;
		}

		const currentId = this.readData();
		if (currentId && typeof currentId === 'object' && currentId.id) {
			return currentId.id;
		} else {
			return this.makeId();
		}

	}

	getEntitiesByAttribute(options, callback) {
		const queryOptions = {
			attribute: {},
			resultsOptions: {
				take: 30,
				sortBy: [{ type: 'attribute', attributeName: 'popularity', order: 'desc' }]
			},
		};

		if (options && typeof options === 'object') {
			for (const key of Object.keys(options)) {
				if (key.includes('Options')) {
					queryOptions[key] = options[key];
				} else if (['skip', 'take', 'facets', 'filter', 'sortBy'].includes(key)) {
					queryOptions.resultsOptions[key] = options[key];
				} else if (['resultsOptions', 'customData', 'attribute', 'relatedResultsOptions', 'spellingSuggestionsOptions', 'relatedQueriesOptions'].includes(key)) {
					queryOptions[key] = options[key];
				}
			}
		}

		const controller = new AbortController();
		const signal = controller.signal;

		this.doFetch(this.settings.url + '/getEntitiesByAttribute', signal, queryOptions, callback);

		return controller;
	}

	getEntities(options, callback) {
		const queryOptions = {
			resultsOptions: {
				take: 30,
				sortBy: [{ type: 'attribute', attributeName: 'popularity', order: 'desc' }]
			},
		};

		if (options && typeof options === 'object') {
			for (const key of Object.keys(options)) {
				if (key.includes('Options')) {
					queryOptions[key] = options[key];
				} else if (['skip', 'take', 'facets', 'filter', 'sortBy'].includes(key)) {
					queryOptions.resultsOptions[key] = options[key];
				} else if (['resultsOptions', 'customData', 'relatedResultsOptions', 'spellingSuggestionsOptions', 'relatedQueriesOptions'].includes(key)) {
					queryOptions[key] = options[key];
				}
			}
		}

		const controller = new AbortController();
		const signal = controller.signal;

		this.doFetch(this.settings.url + '/getEntities', signal, queryOptions, callback);

		return controller;
	}

	search(term, options, callback) {

		const queryOptions = {
			query: term,
			resultsOptions: {
				take: 30
			},
			relatedResultsOptions: {
				take: 0
			},
		};

		if (options && typeof options === 'object') {
			for (const key of Object.keys(options)) {
				if (key.includes('Options')) {
					queryOptions[key] = options[key];
				} else if (['skip', 'take', 'facets', 'filter', 'sortBy'].includes(key)) {
					queryOptions.resultsOptions[key] = options[key];
				} else if (['resultsOptions', 'customData', 'relatedResultsOptions', 'spellingSuggestionsOptions', 'relatedQueriesOptions'].includes(key)) {
					queryOptions[key] = options[key];
				}
			}
		}

		const controller = new AbortController();
		const signal = controller.signal;

		this.doFetch(this.settings.url + '/search', signal, queryOptions, callback);

		return controller;

	}

	autoComplete(term, options, callback) {
		const queryOptions = {
			query: term,
			queriesOptions: {
				skip: 0,
				take: 9
			}
		};

		if (options && typeof options === 'object') {
			if (options.skip) {
				queryOptions.queriesOptions.skip = options.skip;
			}
			if (options.take) {
				queryOptions.queriesOptions.take = options.take;
			}
		}

		const controller = new AbortController();
		const signal = controller.signal;

		this.doFetch(this.settings.url + '/autoComplete', signal, queryOptions, callback);

		return controller;
	}

	instantSearch(term, options = {}, callback) {

		const {
			suggestions = 9,
			instantSearch = 5
		} = options || {};

		const autoCompleteQueryOptions = {
			query: term,
			queriesOptions: {
				skip: 0,
				take: suggestions
			}
		};
		const searchQueryOptions = {
			query: term,
			resultsOptions: {
				skip: 0,
				take: instantSearch
			},
			relatedResultsOptions: {
				skip: 0,
				take: 0
			},
			customData: {
				directSearch: true
			}
		};

		const controller = new AbortController();
		const signal = controller.signal;

		Promise.all([
			suggestions > 0 ? this.doFetchAsync(this.settings.url + '/autoComplete', signal, autoCompleteQueryOptions) : Promise.resolve({ error: false, data: null }),
			instantSearch > 0 ? this.doFetchAsync(this.settings.url + '/search', signal, searchQueryOptions) : Promise.resolve({ error: false, data: null })]).then((values) => {
			const [autoCompleteData, searchData] = values;

			if (typeof callback === 'function') {
				callback({
					autoComplete: autoCompleteData.error ? null : autoCompleteData.data,
					search: searchData.error ? null : searchData.data
				});
			}
		}).catch(() => {
			if (typeof callback === 'function') {
				callback({
					autoComplete: null,
					search: null
				});
			}
		});

		return controller;

	}

	createEvent(eventType, entity, callback) {

		const queue = {
			events: []
		};

		if (entity) {

			for (const singelEntity of (Array.isArray(entity) ? entity : [entity])) {
				if ((typeof singelEntity === 'object') && singelEntity && singelEntity.entity) {
					queue.events.push({
						type: eventType,
						...singelEntity
					});
				} else {
					queue.events.push({
						type: eventType,
						entity: singelEntity
					});
				}
			}

		}

		const controller = new AbortController();
		const signal = controller.signal;

		this.doFetch(this.settings.url + '/createEvents', signal, queue, callback, true);

		return controller;
	}

	doFetch(url, signal, data, callback = () => { }, alwaysRunCallback) {
		fetch(url, {
			signal: signal,
			method: 'POST', // *GET, POST, PUT, DELETE, etc.
			//mode: 'cors', // no-cors, *cors, same-origin
			cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
			//credentials: 'same-origin', // include, *same-origin, omit
			headers: {
				'Api-Version': this.settings.apiVersion,
				'User-Id': this.getId(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
			//redirect: 'follow', // manual, *follow, error
			//referrerPolicy: 'no-referrer-when-downgrade', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
		}).then(async response => {
			if (!response.ok) {
				throw new Error('Response error.');
			}

			const data = await response.json();

			// add loop54 side/version to datalayer for analytics
			const loop54Side = data?.customData?.side || 'unknown';
			dataLayer?.push({
				loop54Side
			});

			return data;
		}).then(callback).catch((error) => {
			if (alwaysRunCallback) {
				if (typeof callback === 'function') {
					callback({ error: true });
				}
			} else {
				if (error.name === 'AbortError') {
					// Aborted
				} else if (typeof callback === 'function') {
					callback({ error: true });
				}
			}
		});
	}

	async doFetchAsync(url, signal, data) {
		try {
			const response = await fetch(url, {
				signal: signal,
				method: 'POST', // *GET, POST, PUT, DELETE, etc.
				//mode: 'cors', // no-cors, *cors, 	same-origin
				cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
				//credentials: 'same-origin', // include, *same-origin, omit
				headers: {
					'Api-Version': this.settings.apiVersion,
					'User-Id': this.getId(),
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(data)
				//redirect: 'follow', // manual, *follow, error
				//referrerPolicy: 'no-referrer-when-downgrade', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
			});

			if (!response.ok) {
				throw new Error('Response error.');
			}

			const responseData = await response.json();
			return {
				error: false,
				data: responseData
			};
		} catch (error) {
			if (error.name === 'AbortError') {
				return {
					error: false,
					data: null
				};
			}
			return {
				error: true,
				data: null
			};
		}
	}
}

export default Loop54Connector;
